import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { creatorVh2hAPI } from '../api/endpoints';
import { FaUsers, FaTrophy, FaCheck, FaClock } from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './CreatorVirtualH2H.css';

const CreatorVirtualH2HPlay = () => {
  const { slug } = useParams();
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [game, setGame] = useState(null);
  const [outcome, setOutcome] = useState('');
  const [stake, setStake] = useState(10);
  const [submitting, setSubmitting] = useState(false);

  const fetchGame = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const res = await creatorVh2hAPI.getBySlug(slug);
      setGame(res.data);
      if (res.data?.min_stake && !stake) setStake(res.data.min_stake);
    } catch (err) {
      if (showLoader) toast.error('Game not found');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [slug, stake]);

  useEffect(() => { fetchGame(true); }, [fetchGame]);

  // Poll while open/live
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchGame(false);
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchGame]);

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) return toast.error('Login to join');
    if (!outcome) return toast.error('Pick an outcome');
    setSubmitting(true);
    try {
      const res = await creatorVh2hAPI.join(slug, { outcome, stake: Number(stake) });
      if (res.data?.success) {
        toast.success('Stake placed!');
        fetchGame(false);
      } else {
        toast.error(res.data?.message || 'Failed to join');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to join');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClaim = async () => {
    try {
      const res = await creatorVh2hAPI.claim(game.id);
      if (res.data?.success) {
        toast.success(`Claimed ${Number(res.data.amount).toFixed(2)} $XPUNT`);
        fetchGame(false);
      } else {
        toast.error(res.data?.message || 'Failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  if (loading) return <div className="cvh2h-loading"><LoadingSpinner size="large" /></div>;
  if (!game) return <div className="cvh2h-empty"><p>Game not found.</p></div>;

  const isCreator = user && String(user.id) === String(game.creator_id);
  const canJoin = game.status === 'open' && !isCreator && !game.my_stake;
  const bd = game.breakdown || {};

  return (
    <div className="cvh2h-play-page">
      <div className="cvh2h-play-header">
        <div className="creator-chip">
          {game.creator_avatar && <img src={game.creator_avatar} alt="" />}
          <span>Hosted by <strong>{game.creator_name}</strong></span>
        </div>
        <span className={`status-tag ${game.status}`}>{game.status.toUpperCase()}</span>
      </div>

      <div className="cvh2h-play-teams">
  <div className="team">
    {game.home_club_logo && <img src={game.home_club_logo} alt="" />}
    <span className="team-name">{game.home_team}</span>
    {game.home_score !== null && <strong className="score">{game.home_score}</strong>}
  </div>
  <span className="vs">vs</span>
  <div className="team away">
     {game.away_score !== null && <strong className="score">{game.away_score}</strong>}
    {game.away_club_logo && <img src={game.away_club_logo} alt="" />}
    <span className="team-name">{game.away_team}</span>
   
  </div>
</div>

      <div className="cvh2h-play-stats">
        <span><FaUsers /> {game.total_players} players</span>
        <span><FaTrophy /> {Number(game.total_pot).toFixed(2)} $XPUNT pot</span>
      </div>

      {/* Breakdown */}
      <div className="breakdown">
        {['Home Win','Draw','Away Win'].map(o => (
          <div key={o} className="breakdown-row">
            <span className="outcome">{o}</span>
            <span className="count">{bd[o]?.players || 0} players</span>
            <span className="total">{Number(bd[o]?.total || 0).toFixed(2)} $XPUNT</span>
          </div>
        ))}
      </div>

      {/* Join form */}
      {canJoin && (
        <form className="join-panel" onSubmit={handleJoin}>
          <h3>Place Your Prediction</h3>
          <div className="outcome-buttons">
            {['Home Win','Draw','Away Win'].map(o => (
              <button
                key={o} type="button"
                className={outcome === o ? 'active' : ''}
                onClick={() => setOutcome(o)}
              >{o}</button>
            ))}
          </div>
          <label>Stake (min {game.min_stake} $XPUNT)</label>
          <input
            type="number" min={game.min_stake} step="1"
            value={stake}
            onChange={(e) => setStake(e.target.value)}
          />
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? <LoadingSpinner size="small" /> : 'Join Game'}
          </button>
        </form>
      )}

      {/* My stake */}
        {game.my_stake?.status === 'won' && !game.my_stake.claimed && (
            <div className="win-summary">
                <div className="win-row">
                <span>Your stake</span>
                <strong>{Number(game.my_stake.stake_amount).toFixed(2)}</strong>
                </div>
                <div className="win-row">
                <span>Profit</span>
                <strong className="positive">+{Number(game.my_stake.winnings - game.my_stake.stake_amount).toFixed(2)}</strong>
                </div>
                <div className="win-row total">
                <span>Claimable payout</span>
                <strong>{Number(game.my_stake.winnings).toFixed(2)} $XPUNT</strong>
                </div>
                <button className="btn btn-primary" onClick={handleClaim}>
                Claim {Number(game.my_stake.winnings).toFixed(2)} $XPUNT
                </button>
            </div>
        )}

       <div className="creator-note">
        If you lose but staked more than the winning pot, the surplus is refunded to you automatically.
     </div>
      {/* Creator preview */}
      {isCreator && (
        <div className="creator-note">
          You are the host. Players are joining via your shared link. Go to your dashboard to Run the match.
        </div>
      )}

    

      {/* Settled result */}
      {game.status === 'completed' && (
        <div className="settled-banner">
          FT: {game.home_team} {game.home_score} - {game.away_score} {game.away_team} — <strong>{game.result}</strong>
        </div>
      )}
      {game.status === 'refunded' && <div className="settled-banner refunded">💸 Refunded — only one side had players.</div>}
      {game.status === 'cancelled' && <div className="settled-banner cancelled">❌ Cancelled — no players joined.</div>}
      {game.status === 'open' && <div className="waiting-note"><FaClock /> Waiting for the host to start the match...</div>}
    </div>
  );
};

export default CreatorVirtualH2HPlay;